export const MAX_CODE_BYTES = 400 * 1024;

export function byteLenUtf8(s: string): number {
  return new TextEncoder().encode(s).length;
}

export function repLabel(score: number): { label: string; color: "success" | "warning" | "error" | "info" } {
  if (score >= 20) return { label: "достаточно доверенно", color: "success" };
  if (score > 5) return { label: "приемлемо", color: "info" };
  if (score <= -5) return { label: "плохая репутация", color: "error" };
  return { label: "репутации нет", color: "warning" };
}

export function mapApiError(e: string): string {
  const map: Record<string, string> = {
    login_required: "Нужно войти.",
    banned: "Аккаунт заблокирован.",
    invalid_json: "Некорректный запрос (JSON).",
    title_required: "Заполни название.",
    code_required: 'Заполни поле "Код".',
    code_not_string: "Поле кода должно быть текстом.",
    code_contains_null: "Код содержит запрещённые символы (binary/null).",
    code_too_large: "Код слишком большой (лимит 400 KB).",
    code_not_mldsl: "Похоже, это не MLDSL код (нет event/func/loop/player).",
    bundle_missing_plan: "Нужен файл plan.json (без него /loadmodule не сможет печатать).",
    bundle_missing_sources: "Нужны исходники .mldsl (для чтения и редактирования).",

    upload_token_required: "Нет upload‑токена. Сгенерируй его в профиле (для VS Code/CLI).",
    bad_filename: "Некорректное имя файла (слишком длинное или содержит запрещённые символы).",
    files_required: "Нужно прикрепить файлы.",
    files_too_many: "Слишком много файлов (лимит 30).",

    connect_code_required: "Не указан code для подключения.",
    connect_code_invalid: "Неверный код подключения (или уже удалён).",
    connect_code_used: "Этот код подключения уже использован.",
    connect_code_expired: "Код подключения истёк, создай новый.",
    q_required: "Введите строку поиска.",
    q_too_short: "Слишком короткий запрос (минимум 2 символа).",
    q_too_long: "Слишком длинный запрос.",
    user_not_found: "Пользователь не найден.",

    profile_username_too_short: "Имя слишком короткое (минимум 2 символа).",
    profile_avatar_bad_url: "Аватар должен быть ссылкой http/https (или оставь пустым).",
    profile_name_color_bad: "Цвет ника должен быть в формате #RRGGBB (или оставь пустым для дефолта).",
    profile_topbar_color_bad: "Цвет топбара должен быть в формате #RRGGBB (или оставь пустым для дефолта).",

    postId_required: "Не указан пост.",
    not_allowed: "Недостаточно прав для этой операции.",
    self_vote_forbidden: "Нельзя оценивать собственный пост.",
    comment_rate_limited: "Слишком часто. Комментарии можно отправлять раз в минуту.",
    links_require_trusted_author: "Ссылки в постах доступны только админам или авторам с 25+ лайков.",
    rate_limited: "Слишком часто. Подожди немного и повтори.",
    missing_idempotency_key: "Запрос отклонен: отсутствует Idempotency-Key.",
    idempotency_conflict: "Повтор запроса с тем же ключом, но другим содержимым.",
  };
  return map[e] || e;
}

export async function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  ta.remove();
}

export function downloadTextFile(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

function hexToRgb(hex: string) {
  const h = hex.replace("#", "").trim();
  const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(v, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpColor(a: string, b: string, t: number) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  const r = Math.round(lerp(A.r, B.r, t));
  const g = Math.round(lerp(A.g, B.g, t));
  const b2 = Math.round(lerp(A.b, B.b, t));
  return `rgb(${r}, ${g}, ${b2})`;
}

export type ScoreRampOverrides = { zeroBase?: string; zeroAccent?: string };

export function scoreColorRamp(score: number, overrides?: ScoreRampOverrides) {
  const min = -5;
  const zero = 0;
  const mid = 25;
  const max = 100;

  const cRed = "#ef4444";
  const cZero = overrides?.zeroBase || "#99adff";
  const cZero2 = overrides?.zeroAccent || "#b3c0ff";
  const cGreen = "#22c55e";
  const cBlue = "#3b82f6";

  if (score <= zero) {
    const t = clamp((score - min) / (zero - min), 0, 1);
    const base = lerpColor(cRed, cZero, t);
    const accent = lerpColor(cRed, cZero2, clamp(t + 0.18, 0, 1));
    return { base, accent };
  }
  if (score <= mid) {
    const t = clamp((score - zero) / (mid - zero), 0, 1);
    const base = lerpColor(cZero, cGreen, t);
    const accent = lerpColor(cZero2, cGreen, clamp(t + 0.18, 0, 1));
    return { base, accent };
  }

  const t = clamp((score - mid) / (max - mid), 0, 1);
  const base = lerpColor(cGreen, cBlue, t);
  const accent = lerpColor(cGreen, cBlue, clamp(t + 0.22, 0, 1));
  return { base, accent };
}
